import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useStore } from '../components/state/zustand-store';
import { useState } from 'react';
import { Messages } from '../utils/message-constants';
import { saveGroupLabels, UserKeys } from '../api/users-api';
import { handleErrors } from '../utils/errors';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../utils/roles/client-roles';
import GeneralDialog from './general-dialog';
import ChangeLogDialog from './change-log-dialog';
import { Controller, useForm } from 'react-hook-form';
import GroupLabelsCheckboxes from './group-labels-checkboxes';
import { groupLabels } from './group-labels-config';
import axios from 'axios';
import { ApiException } from '../../types';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface UpdateGroupLabelsPayload {
  groupEmail: string;
  labels: { [key: string]: string };
  changeLogMessage?: string;
}

interface EditGroupLabelsDialogProps {
  open: boolean;
  handleClose: () => void;
  groupEmail: string;
  groupId: string;
  currentLabels: { [key: string]: string };
  changeLogRoles: string[];
  directoryRoles: string[];
  accessGranted: boolean;
}

export default function EditGroupLabelsDialog({
  open,
  handleClose,
  groupEmail,
  groupId,
  currentLabels,
  changeLogRoles,
  directoryRoles,
  accessGranted,
}: EditGroupLabelsDialogProps) {
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const defaultLabels = groupLabels.reduce(
    (acc, label) => {
      acc[label.name] =
        Object.prototype.hasOwnProperty.call(currentLabels, label.key) || false;
      return acc;
    },
    {} as { [key: string]: boolean }
  );

  function handleError(error: unknown) {
    if (axios.isAxiosError(error)) {
      const errorResponse = error.response?.data as ApiException;
      if (errorResponse.message?.includes('Error(2021)')) {
        handleErrors({
          error,
          snackbarMessage:
            groupEmail +
            ' has one or more child groups are not Security Groups.',
        });
      } else {
        handleErrors({
          error,
          snackbarMessage: Messages.Errors.Network.Updating('group labels'),
        });
      }
    } else {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating('group labels'),
      });
    }
  }

  const {
    control,
    handleSubmit: handleFormSubmit,
    formState: { isDirty },
    getValues,
  } = useForm({
    defaultValues: { labels: defaultLabels },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (data: UpdateGroupLabelsPayload) =>
      saveGroupLabels(
        data.labels,
        groupEmail,
        groupId,
        data.changeLogMessage ?? ''
      ),
    onSuccess: (_data, variables) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Group labels'),
      });
      queryClient.setQueryData(
        [UserKeys.GroupLabels, groupEmail],
        variables.labels
      );
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => handleError(error),
    onSettled: () => {
      setOpenChangeLogDialog(false);
    },
  });

  const performMutation = (
    data: { labels: { [key: string]: boolean } },
    currentChangeLogMessage: string
  ) => {
    const newLabels: { [key: string]: string } = {};
    groupLabels.forEach((label) => {
      if (data.labels[label.name]) {
        newLabels[label.key] = '';
      }
    });

    const payload: UpdateGroupLabelsPayload = {
      groupEmail,
      labels: newLabels,
      changeLogMessage: currentChangeLogMessage,
    };
    mutate(payload);
  };
  const onSubmit = (data: { labels: { [key: string]: boolean } }) => {
    if (
      isAbleToSeeUIElement(changeLogRoles, DirectoryRoles.EditGroupLabels.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      performMutation(data, '');
    }
  };

  const canEditLabels = isAbleToSeeUIElement(
    directoryRoles,
    DirectoryRoles.EditGroupLabels.value
  );

  const disabledStates = groupLabels.reduce(
    (acc, label) => {
      acc[label.name] =
        !!label.isUnremovable &&
        Object.prototype.hasOwnProperty.call(currentLabels, label.key);
      return acc;
    },
    {} as { [key: string]: boolean }
  );

  return (
    <GeneralDialog
      open={open}
      handleClose={handleClose}
      title="Edit Group Labels"
      maxWidth="md"
      dialogContent={
        <Stack sx={{ px: 4 }} gap={2}>
          <Typography>
            Select the labels you want to apply to this group:
          </Typography>
          <Controller
            name="labels"
            control={control}
            render={({ field }) => (
              <GroupLabelsCheckboxes
                checkedStates={field.value}
                onStateChange={(name, checked) => {
                  field.onChange({ ...field.value, [name]: checked });
                }}
                disabledStates={disabledStates}
                accessGranted={accessGranted || false}
              />
            )}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => performMutation(getValues(), changeLogMessage)}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Save',
        action: handleFormSubmit(onSubmit),
        loading: isPending,
        disabled: !canEditLabels || !isDirty,
        hasTooltip: !canEditLabels,
        tooltipTitle: Messages.Errors.Permission.Acting(
          DirectoryRoles.EditGroupLabels.name
        ),
      }}
    />
  );
}
